import { useState } from "react";

import {
  faArrowRight,
  faRectangleList,
} from "@fortawesome/pro-regular-svg-icons";
import {
  useController,
  useFormContext,
  useFormState,
  useWatch,
} from "react-hook-form";

import { DateTimePickerDropdown } from "src/base-components/DateTimePickerDropdown";
import { Pill, PillVariants } from "src/base-components/Pill";
import { Select } from "src/base-components/Select";
import { useGetCasePropertyIcon } from "src/cases/common/useGetCasePropertyIcon";
import { CaseUrlPropertyInput } from "src/cases/createCase/CaseUrlPropertyInput";
import {
  CreateCaseFormValues,
  CreateCaseStep,
} from "src/cases/createCase/createCaseSteps";
import { OpenCasesModal } from "src/cases/createCase/OpenCasesModal";
import { SearchableEntityRefInput } from "src/cases/createCase/SearchableEntityRefInput";
import { SearchableEventRefInput } from "src/cases/createCase/SearchableEventRefInput";
import {
  EntityWithOpenCases,
  useOpenCasesForMappedEntities,
} from "src/cases/createCase/useOpenCasesForMappedEntities";
import { FormStepWrapper } from "src/cases/templates/FormStepWrapper";
import { CasePropertiesValue } from "src/clients/case-management";
import { Callout } from "src/design-system/Callout";
import { EmptyState } from "src/design-system/EmptyState";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Icon } from "src/design-system/Icon";
import { Input } from "src/design-system/Input";
import { useModal } from "src/design-system/Modal";
import { DashboardPageParams } from "src/router/urls";
import { UrlValueType } from "src/utils/url";
import { useParamsDecode } from "src/utils/useParamsDecode";

type DuplicateRefType = "entity" | "event" | "both";

const DUPLICATE_WARNING_LABELS: Record<DuplicateRefType, string> = {
  entity: "Entity",
  event: "Event",
  both: "Entity or Event",
};

const getDuplicateRefType = (
  caseProperties: Record<string, CasePropertiesValue>,
  watchedValues: Record<string, unknown>,
): DuplicateRefType | null => {
  const entityRefValues: string[] = [];
  const eventRefValues: string[] = [];

  for (const [key, property] of Object.entries(caseProperties)) {
    const value = watchedValues[key];
    if (typeof value !== "string" || value === "") continue;

    if (property.type === "entity_ref") {
      entityRefValues.push(value);
    } else if (property.type === "event_ref") {
      eventRefValues.push(value);
    }
  }

  const hasEntityDupes =
    entityRefValues.length !== new Set(entityRefValues).size;
  const hasEventDupes = eventRefValues.length !== new Set(eventRefValues).size;

  if (hasEntityDupes && hasEventDupes) return "both";
  if (hasEntityDupes) return "entity";
  if (hasEventDupes) return "event";
  return null;
};

const useDuplicateRefWarning = (
  caseProperties: Record<string, CasePropertiesValue> | undefined,
) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const watchedValues = useWatch<CreateCaseFormValues, "case_properties">({
    name: "case_properties",
  });

  const duplicateType =
    caseProperties && watchedValues
      ? getDuplicateRefType(caseProperties, watchedValues)
      : null;

  if (isDismissed && !duplicateType) {
    setIsDismissed(false);
  }

  return {
    show: duplicateType !== null && !isDismissed,
    label: duplicateType ? DUPLICATE_WARNING_LABELS[duplicateType] : "",
    dismiss: () => setIsDismissed(true),
  };
};

type CreateCasePropertiesStepProps = {
  caseProperties: Record<string, CasePropertiesValue> | undefined;
  step: CreateCaseStep;
};

export const CreateCasePropertiesStep: React.FC<
  CreateCasePropertiesStepProps
> = ({ caseProperties, step }) => {
  const getPropertyIcon = useGetCasePropertyIcon();
  const form = useFormContext<CreateCaseFormValues>();
  const { wsId, orgId } = useParamsDecode<DashboardPageParams>();

  const entries = caseProperties ? Object.entries(caseProperties) : [];
  const duplicateWarning = useDuplicateRefWarning(caseProperties);
  const { entities: entitiesWithOpenCases } =
    useOpenCasesForMappedEntities(caseProperties);
  const openCasesModal = useModal<EntityWithOpenCases>();

  return (
    <FormStepWrapper description={step.description} title={step.formTitle}>
      {entitiesWithOpenCases.map((entity) => (
        <Callout
          key={`${entity.entityType}-${entity.entityId}`}
          action={{
            text: "View Cases",
            onClick: () => openCasesModal.openModal(entity),
          }}
          dataLoc={`open-cases-warning-${entity.entityId}`}
          variant="warning"
        >
          {`${entity.displayName} already has open Cases. Review existing cases to avoid duplicates.`}
        </Callout>
      ))}
      {openCasesModal.data && (
        <OpenCasesModal
          afterLeave={openCasesModal.afterLeave}
          entityDisplayName={openCasesModal.data.displayName}
          groups={openCasesModal.data.groups}
          isOpen={openCasesModal.isOpen}
          orgId={orgId}
          wsId={wsId}
          onClose={openCasesModal.closeModal}
        />
      )}
      {duplicateWarning.show && (
        <Callout
          action={{ text: "Dismiss", onClick: duplicateWarning.dismiss }}
          variant="warning"
        >
          {`The same ${duplicateWarning.label} is mapped to multiple properties, review the mappings to confirm the values are correct`}
        </Callout>
      )}
      {entries.length === 0 ? (
        <EmptyState
          description="No properties have been configured for this case template"
          headline="No properties"
          icon={faRectangleList}
          variant="default"
        />
      ) : (
        <div className="flex flex-col gap-3">
          {entries.map(([key, property]) => (
            <PropertyRow
              key={key}
              form={form}
              getPropertyIcon={getPropertyIcon}
              property={property}
              propertyKey={key}
            />
          ))}
        </div>
      )}
    </FormStepWrapper>
  );
};

const PropertyRow: React.FC<{
  propertyKey: string;
  property: CasePropertiesValue;
  form: ReturnType<typeof useFormContext<CreateCaseFormValues>>;
  getPropertyIcon: ReturnType<typeof useGetCasePropertyIcon>;
}> = ({ propertyKey, property, form, getPropertyIcon }) => {
  const icon = getPropertyIcon(property);

  return (
    <div className="flex w-full items-start">
      <div className="w-1/2">
        <Input
          disabled
          placeholder={property.display_name}
          prefixIcon={{ icon }}
        />
      </div>
      <div className="mx-3 mt-1.5 flex-shrink-0">
        <Icon icon={faArrowRight} size="xs" />
      </div>
      <div className="w-1/2 min-w-0">
        <PropertyInput
          form={form}
          property={property}
          propertyKey={propertyKey}
        />
      </div>
    </div>
  );
};

const PropertyInput: React.FC<{
  propertyKey: string;
  property: CasePropertiesValue;
  form: ReturnType<typeof useFormContext<CreateCaseFormValues>>;
}> = ({ propertyKey, property, form }) => {
  const { errors } = useFormState<CreateCaseFormValues>();
  const fieldName = `case_properties.${propertyKey}` as const;
  const fieldError = errors.case_properties?.[propertyKey];
  const errored = !!fieldError;
  const errorMessage =
    fieldError?.message && typeof fieldError.message === "string"
      ? fieldError.message
      : undefined;

  return (
    <>
      {property.type === "string" ||
      property.type === "integer" ||
      property.type === "number" ? (
        <Input
          error={errored}
          placeholder="Enter value"
          {...form.register(fieldName)}
        />
      ) : (
        <ControlledPropertyInput
          errored={errored}
          errorMessage={errorMessage}
          form={form}
          property={property}
          propertyKey={propertyKey}
        />
      )}
      {errorMessage && property.type !== "url" && (
        <ErrorHint>{errorMessage}</ErrorHint>
      )}
    </>
  );
};

const ControlledPropertyInput: React.FC<{
  propertyKey: string;
  property: CasePropertiesValue;
  form: ReturnType<typeof useFormContext<CreateCaseFormValues>>;
  errored: boolean;
  errorMessage?: string;
}> = ({ propertyKey, property, form, errored, errorMessage }) => {
  const fieldName = `case_properties.${propertyKey}` as const;
  const { field } = useController({ control: form.control, name: fieldName });

  switch (property.type) {
    case "datetime":
      return (
        <DateTimePickerDropdown
          errored={errored}
          placeholder="Select a date and time"
          fullWidth
          value={
            typeof field.value === "string"
              ? new Date(field.value)
              : (field.value as Date | undefined)
          }
          allowFutureDates
          onChange={(value) => field.onChange(value)}
          onReset={() => field.onChange(null)}
        />
      );

    case "enum":
      return (
        <Select
          buttonRenderer={(option) =>
            option ? (
              <Pill size="sm" variant={option.pillVariant ?? "gray"} maxWidth>
                <Pill.Text>{option.displayValue ?? option.value}</Pill.Text>
              </Pill>
            ) : undefined
          }
          dataLoc={`create-case-property-${propertyKey}`}
          errored={errored}
          options={property.values.map((v) => ({
            key: v.value,
            value: v.display_name || v.value,
            pillVariant: (v.color as PillVariants) ?? undefined,
          }))}
          placeholder="Select value"
          value={(field.value as string | undefined) ?? ""}
          onChange={(value) => {
            field.onChange(field.value === value ? null : value);
          }}
        />
      );

    case "tags":
      return (
        <Select
          dataLoc={`create-case-property-${propertyKey}`}
          errored={errored}
          options={property.values.map((v) => ({
            key: v.value,
            value: v.display_name || v.value,
            pillVariant: (v.color as PillVariants) ?? undefined,
          }))}
          placeholder="Select value(s)"
          value={(field.value as string[]) ?? []}
          multiple
          onChange={field.onChange}
        />
      );

    case "entity_ref":
      return (
        <SearchableEntityRefInput
          entityType={property.entity_type}
          errored={errored}
          value={(field.value as string) ?? ""}
          onChange={field.onChange}
        />
      );

    case "event_ref":
      return (
        <SearchableEventRefInput
          errored={errored}
          eventType={property.event_type}
          value={(field.value as string) ?? ""}
          onChange={field.onChange}
        />
      );

    case "url":
      return (
        <CaseUrlPropertyInput
          errored={errored}
          errorMessage={errorMessage}
          value={field.value as UrlValueType | null}
          onBlur={field.onBlur}
          onChange={field.onChange}
        />
      );

    default:
      return null;
  }
};
